import {
  ImportPriceOnePaymentRow,
  ImportPricePvpRow,
  ImportPriceRow,
  ImportVariant,
} from './types';

type ParseResult =
  | { success: true; rows: ImportPriceRow[] }
  | { success: false; error: string };

const VARIANT_COLUMNS: Record<ImportVariant, string[]> = {
  pvp: ['publication_id', 'precio_pvp', 'precio_pv_campana'],
  'one-payment': ['publication_id', 'precio_1_pago'],
};

const CSV_DELIMITER = ';';

const detectDelimiter = (line: string): string => {
  const semicolonCount = (line.match(/;/g) || []).length;
  const commaCount = (line.match(/,/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

const parseCsvLine = (line: string, delimiter = CSV_DELIMITER): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const normalizeHeader = (header: string) => header.trim().toLowerCase();

const parsePublicationId = (value: string): number | string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const asNumber = Number(trimmed);
  if (!Number.isNaN(asNumber) && trimmed === String(asNumber)) return asNumber;
  return trimmed;
};

const parsePrice = (value: string, fieldName: string, rowIndex: number): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(',', '.'));
  if (Number.isNaN(parsed)) {
    throw new Error(`Fila ${rowIndex}: "${fieldName}" debe ser un número válido.`);
  }
  return parsed;
};

export const parseImportPricesCsv = async (
  file: File,
  variant: ImportVariant,
): Promise<ParseResult> => {
  try {
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      return { success: false, error: 'El archivo debe contener encabezados y al menos una fila de datos.' };
    }

    const requiredColumns = VARIANT_COLUMNS[variant];
    const delimiter = detectDelimiter(lines[0]);
    const headers = parseCsvLine(lines[0], delimiter).map(normalizeHeader);
    const missingColumns = requiredColumns.filter(
      (column) => !headers.includes(column.toLowerCase()),
    );

    if (missingColumns.length > 0) {
      return {
        success: false,
        error: `Faltan columnas requeridas: ${missingColumns.join(', ')}.`,
      };
    }

    const columnIndex = Object.fromEntries(headers.map((header, index) => [header, index]));
    const rows: ImportPriceRow[] = [];

    for (let i = 1; i < lines.length; i += 1) {
      const values = parseCsvLine(lines[i], delimiter);
      const rowNumber = i + 1;

      const publicationId = parsePublicationId(values[columnIndex.publication_id] ?? '');
      if (publicationId == null) {
        return { success: false, error: `Fila ${rowNumber}: "publication_id" es obligatorio.` };
      }

      if (variant === 'pvp') {
        const precioPvp = parsePrice(values[columnIndex.precio_pvp] ?? '', 'precio_pvp', rowNumber);
        const precioPvColumn = columnIndex.precio_pv_campana ?? columnIndex.precio_pv;
        const precioPv = parsePrice(values[precioPvColumn] ?? '', 'precio_pv', rowNumber);

        if (precioPvp == null || precioPv == null) {
          return {
            success: false,
            error: `Fila ${rowNumber}: "precio_pvp" y "precio_pv" son obligatorios.`,
          };
        }

        rows.push({
          publication_id: publicationId,
          precio_pvp: precioPvp,
          precio_pv: precioPv,
        });
      } else {
        const precioUnPago = parsePrice(
          values[columnIndex.precio_1_pago] ?? '',
          'precio_1_pago',
          rowNumber,
        );

        if (precioUnPago == null) {
          return { success: false, error: `Fila ${rowNumber}: "precio_1_pago" es obligatorio.` };
        }

        rows.push({
          publication_id: publicationId,
          precio_1_pago: precioUnPago,
        });
      }
    }

    if (rows.length === 0) {
      return { success: false, error: 'El archivo no contiene filas de datos para importar.' };
    }

    return { success: true, rows };
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'No se pudo leer el archivo CSV.',
    };
  }
};

export const isPvpImportRow = (row: ImportPriceRow): row is ImportPricePvpRow =>
  'precio_pvp' in row;
